import { useLayoutEffect } from 'react';
import type { SeoDescriptor } from './landingSeo';

/** Marks the alternate links this hook owns, so cleanup removes only those. */
const OWNED = 'data-seo-alternate';

type Restore = () => void;

/** Set an attribute, returning a function that puts the old value back. */
function setAttr(el: Element, name: string, value: string): Restore {
  const previous = el.getAttribute(name);
  el.setAttribute(name, value);
  return () => {
    if (previous === null) el.removeAttribute(name);
    else el.setAttribute(name, previous);
  };
}

/**
 * Update a singleton head tag in place, creating it only if index.html did not
 * already ship one. Appending instead would produce two canonicals — which
 * Google reads as none.
 */
function upsert(
  selector: string,
  create: () => HTMLElement,
  attr: string,
  value: string,
): Restore {
  const existing = document.head.querySelector(selector);
  if (existing) return setAttr(existing, attr, value);

  const created = create();
  created.setAttribute(attr, value);
  document.head.appendChild(created);
  return () => created.remove();
}

function meta(name: string, kind: 'name' | 'property'): () => HTMLElement {
  return () => {
    const el = document.createElement('meta');
    el.setAttribute(kind, name);
    return el;
  };
}

/**
 * Applies a locale's head tags and restores the previous ones on unmount.
 *
 * useLayoutEffect rather than useEffect so the head is correct before the
 * browser paints, which keeps the tab title from flickering through the
 * English default on /de.
 */
export function useSeoHead(descriptor: SeoDescriptor): void {
  const {
    title,
    description,
    canonical,
    ogUrl,
    ogTitle,
    ogDescription,
    twitterTitle,
    twitterDescription,
    ogLocale,
    alternates,
  } = descriptor;

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const previousTitle = document.title;
    document.title = title;

    const restores: Restore[] = [
      upsert(
        'meta[name="description"]',
        meta('description', 'name'),
        'content',
        description,
      ),
      upsert(
        'link[rel="canonical"]',
        () => {
          const el = document.createElement('link');
          el.setAttribute('rel', 'canonical');
          return el;
        },
        'href',
        canonical,
      ),
      upsert('meta[property="og:url"]', meta('og:url', 'property'), 'content', ogUrl),
      upsert(
        'meta[property="og:title"]',
        meta('og:title', 'property'),
        'content',
        ogTitle,
      ),
      upsert(
        'meta[property="og:description"]',
        meta('og:description', 'property'),
        'content',
        ogDescription,
      ),
      // twitter:title/description mirror the og pair but live under a
      // different tag name — previously untouched here, so they stayed
      // English forever regardless of locale.
      upsert(
        'meta[name="twitter:title"]',
        meta('twitter:title', 'name'),
        'content',
        twitterTitle,
      ),
      upsert(
        'meta[name="twitter:description"]',
        meta('twitter:description', 'name'),
        'content',
        twitterDescription,
      ),
      // index.html ships no og:locale tag, so this always exercises the
      // create branch of upsert() (and the removal branch on cleanup).
      upsert(
        'meta[property="og:locale"]',
        meta('og:locale', 'property'),
        'content',
        ogLocale,
      ),
    ];

    // Alternates have no static counterpart, so they are created wholesale and
    // cleared first — re-running must replace them, not stack them up.
    document.head.querySelectorAll(`link[${OWNED}]`).forEach((el) => el.remove());
    for (const alternate of alternates) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', alternate.hreflang);
      link.setAttribute('href', alternate.href);
      link.setAttribute(OWNED, '');
      document.head.appendChild(link);
    }

    return () => {
      document.title = previousTitle;
      for (const restore of restores.reverse()) restore();
      document.head
        .querySelectorAll(`link[${OWNED}]`)
        .forEach((el) => el.remove());
    };
  }, [
    title,
    description,
    canonical,
    ogUrl,
    ogTitle,
    ogDescription,
    twitterTitle,
    twitterDescription,
    ogLocale,
    alternates,
  ]);
}
